import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import Image from 'next/image';
import { Circle, Square, Video, Tv, Maximize, Minimize, ExternalLink, Cast, X } from 'lucide-react';

interface IPTVPlayerProps {
  url: string;
  title?: string;
  autoPlay?: boolean;
  recordingQuality?: 'low' | 'medium' | 'high';
  hideControls?: boolean;
  muted?: boolean;
  isPremium?: boolean;
}

export const IPTVPlayer: React.FC<IPTVPlayerProps> = ({ 
  url, 
  title, 
  autoPlay = true,
  recordingQuality = 'medium',
  hideControls = false,
  muted = false,
  isPremium = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [modalMessage, setModalMessage] = useState<{title: string, message: string} | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const togglePip = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (video.requestPictureInPicture) {
        await video.requestPictureInPicture();
      } else if ((video as any).webkitSetPresentationMode) {
        // Fallback for older Safari
        (video as any).webkitSetPresentationMode('picture-in-picture');
      }
    } catch (error) {
      console.error("PiP failed", error);
    }
  };

  const toggleCast = async () => {
    if (!isPremium) {
      setModalMessage({
        title: "Premium Feature",
        message: "Casting to a Smart TV or external device is exclusively available for Premium members. Please upgrade your plan to enjoy big screen entertainment!"
      });
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    try {
      // Prefer Apple's native AirPlay picker first if available (fixes Safari issues)
      if (typeof (video as any).webkitShowPlaybackTargetPicker === 'function') {
        (video as any).webkitShowPlaybackTargetPicker();
      } 
      // Fallback to W3C Remote Playback API (Chrome / Android)
      else if ('remote' in video && typeof (video as any).remote.prompt === 'function') {
        await (video as any).remote.prompt();
      } else {
        setModalMessage({
          title: "Casting Not Supported",
          message: "Casting is not supported or not currently available in this browser. Please try using a compatible browser like Chrome for Android or Safari."
        });
      }
    } catch (error: any) {
      console.error("Cast failed", error);
      
      const isDismissed = error?.message?.toLowerCase().includes('dismiss') || 
                          error?.name === 'NotAllowedError' ||
                          error?.name === 'NotFoundError';

      if (isDismissed) {
        // Chrome Desktop often immediately rejects remote.prompt() if Cast SDK isn't fully integrated
        // Give the user a premium instructional message rather than failing silently
        setModalMessage({
          title: "Cast via Browser",
          message: "To cast to your TV from this device, please right-click anywhere on the video and select 'Cast...', or use the Cast option in your browser's main menu."
        });
      } else {
        setModalMessage({
          title: "Casting Error",
          message: `Failed to start casting: ${error?.message || 'Unknown error'}`
        });
      }
    }
  };

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording]);

  const startRecording = () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      // Reset recording time
      setRecordingTime(0);
      // @ts-ignore - captureStream is standard but TS might complain depending on version
      const stream = video.captureStream ? video.captureStream() : (video as any).mozCaptureStream ? (video as any).mozCaptureStream() : null;
      
      if (!stream) {
        setModalMessage({
          title: "Recording Not Supported",
          message: "Recording is not supported in this browser."
        });
        return;
      }

      const options: MediaRecorderOptions = { 
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: recordingQuality === 'high' ? 5000000 : recordingQuality === 'medium' ? 2500000 : 1000000
      };
      
      if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
        options.mimeType = 'video/webm;codecs=vp8,opus';
        if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
          options.mimeType = 'video/webm';
        }
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title || 'recording'}_${new Date().getTime()}.webm`;
        a.click();
        URL.revokeObjectURL(url);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Failed to start recording:", err);
      setModalMessage({
        title: "Recording Error",
        message: "Failed to start recording. This might be due to CORS restrictions on the stream."
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [h, m, s].map(v => v.toString().padStart(2, '0')).join(':');
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    // Mixed Content Bypass: Try to upgrade http to https if we are on an https site
    // This often works for CDNs like edgesuite, cloudfront, etc.
    const processedUrl = url.startsWith('http://') && typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? url.replace('http://', 'https://')
      : url;

    let hls: Hls | null = null;

    const playVideo = () => {
      if (autoPlay) {
        video.play().catch(e => console.log("Auto-play blocked or failed", e));
      }
    };

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = processedUrl;
      video.addEventListener('loadedmetadata', playVideo);
    } else if (Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hls.loadSource(processedUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, playVideo);
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error("Fatal network error encountered, trying to recover", data);
              hls?.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error("Fatal media error encountered, trying to recover", data);
              hls?.recoverMediaError();
              break;
            default:
              console.error("Fatal error, cannot recover", data);
              hls?.destroy();
              break;
          }
        }
      });
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
      video.removeEventListener('loadedmetadata', playVideo);
    };
  }, [url, autoPlay]);

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full bg-black flex items-center justify-center overflow-hidden group [will-change:transform]"
    >
      <video
        ref={videoRef}
        className={`w-full h-full object-contain ${!url ? 'hidden' : 'block'}`}
        controls={!hideControls}
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        webkit-playsinline="true"
        disablePictureInPicture={false}
      />
      {!url && (
        <div className="flex flex-col items-center justify-center text-gray-400 w-full h-full animate-in fade-in duration-500">
          <div className="mb-8 relative">
            <div className="absolute inset-0 bg-indigo-600/20 blur-2xl rounded-full animate-pulse" />
            <div className="relative w-24 h-24 md:w-32 md:h-32 bg-gray-900 rounded-3xl flex items-center justify-center shadow-2xl border border-white/10 overflow-hidden transition-all duration-1000">
              {/* CSS Fallback */}
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <Tv size={48} className="text-indigo-500/10" />
              </div>
              <img 
                src="/app-icon.png"
                alt="IPTV GO" 
                width="80"
                height="80"
                draggable="false"
                className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-[0_0_10px_rgba(79,70,229,0.3)] relative z-10"
              />
            </div>
          </div>
          <div className="text-lg md:text-xl font-medium text-center px-10 max-w-xl leading-relaxed text-gray-500">
            Select a channel from the list to start streaming
          </div>
        </div>
      )}
      {title && url && !hideControls && (
        <div className="absolute top-4 left-4 flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-black/50 backdrop-blur-md px-4 py-2 rounded-lg text-white">
            {title}
          </div>
          
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg backdrop-blur-md transition-all duration-300 ${
              isRecording 
                ? 'bg-red-600/80 hover:bg-red-600 text-white animate-pulse' 
                : 'bg-black/50 hover:bg-indigo-600/80 text-white'
            }`}
            title={isRecording ? "Stop Recording" : "Start Recording"}
            aria-label={isRecording ? "Stop Recording" : "Start Recording"}
          >
            {isRecording ? (
              <>
                <Square className="w-4 h-4 fill-current" />
                <span className="text-sm font-medium">{formatTime(recordingTime)}</span>
              </>
            ) : (
              <>
                <Circle className="w-4 h-4 fill-red-500" />
                <span className="text-sm font-medium">Record</span>
              </>
            )}
          </button>

          <button
            onClick={togglePip}
            className="flex items-center gap-2 px-4 py-2 rounded-lg backdrop-blur-md bg-black/50 hover:bg-indigo-600/80 text-white transition-all duration-300"
            title="Picture in Picture"
            aria-label="Picture in Picture"
          >
            <ExternalLink className="w-4 h-4" />
          </button>

          <button
            onClick={toggleCast}
            className="flex items-center gap-2 px-4 py-2 rounded-lg backdrop-blur-md bg-black/50 hover:bg-indigo-600/80 text-white transition-all duration-300"
            title="Cast to Device"
            aria-label="Cast to Device"
          >
            <Cast className="w-4 h-4" />
          </button>

          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-2 px-4 py-2 rounded-lg backdrop-blur-md bg-black/50 hover:bg-indigo-600/80 text-white transition-all duration-300"
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? (
              <Minimize className="w-4 h-4" />
            ) : (
              <Maximize className="w-4 h-4" />
            )}
          </button>
        </div>
      )}
      
      {isRecording && (
        <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-600/20 backdrop-blur-md px-3 py-1 rounded-full border border-red-500/50">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-xs font-bold text-red-500 uppercase tracking-wider">REC</span>
        </div>
      )}

      {/* Premium Notification Modal */}
      {modalMessage && (
        <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-white">{modalMessage.title}</h3>
              <button 
                onClick={() => setModalMessage(null)}
                className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">{modalMessage.message}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setModalMessage(null)}
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-all shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
